import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import { PhoneInput } from "react-international-phone";
import {ReactTags} from 'react-tag-autocomplete';
import 'react-international-phone/style.css'

import GenerationsSelect from "../../../layout/select/generation";
import Address from "../../../layout/address";
import ConditionalButton from "../../../layout/conditional-button";
import Permissions from "../../../layout/permissions";
import ErrorModal from "../../../layout/modal";
import ResetModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getUser, apply as applyToInstance, save as saveUser } from "../../../actions/users";
import { list as listPermissions, user as userPermissions, grant, deny } from "../../../actions/permissions";
import { save as savePerson } from "../../../actions/people";
import { reset as resetPassword } from "../../../actions/auth";

class Detail extends React.Component {
    
    constructor(props) {
        super(props);
        this.props.loading(true);
        this.state = {
            ...this.propsToState(props),
            modal: {
                error: {
                    show: false
                },
                reset: {
                    show: false
                }
            },
            whitelistArray: []
        };
        this.defaultCountry = "us";
        this.props.pageInfo({ title: "User", breadcrumb: `[New User]` });
        this.changes = {};   
    }

    propsToState = (props) => {
        const { generation, firstName, middleName, lastName, Residence, phone, fax, permisions } = props
        return {
            generation, firstName, middleName, lastName, Residence, phone, fax, permisions
        }
    }

    get uuid() {
        return this.props.params.uuid;
    }

    onGenerationChange = (e) => {
        this.onChange({ target: { name: "generation", value: e.value } });
    }

    loadData = async () => {
        this.props.loading(true);
        const permissions = (await this.props.listPermissions()).items;
        if (isGuid(this.uuid)) {
            const user = await this.props.getUser({ uuid: this.uuid });
            const userPermissions = await this.props.userPermissions({ uuid: user.userUuid });

            if (user) {
                this.setState(prev => ({
                    ...prev,
                    ...user,
                    originalEmail: user.email,
                    permissions,
                    sources: [...userPermissions],
                    active: [...userPermissions],
                    whitelistArray: (user.whitelist ?? "")
                        .split(/[,:]/)
                        .map(s => s.trim())
                        .filter(Boolean)
                        .map(v => ({ label: v, value: v }))
                }));
                await this.props.pageInfo({ title: `User (${user.email})`, breadcrumb: `${user.email}` });
            }
        } else {
            const userPermissions = permissions
                .filter(item => item.default === true)   // keep only objects where value = 1
                .map(item => item.name);            // extract the "name" property
            this.setState({
                whitelist: "ALL",
                whitelistArray: [{ label: "ALL", value: "ALL" }],
                permissions,
                sources: [...userPermissions],
                active: [...userPermissions]
            });
        }
        this.props.loading(false);
    }

    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    }

    isExistingUserResponse = (u) => {
        if (!u || typeof u !== "object") return false;
        if (u.success === false) return false;
        return Boolean(u.userUuid || u.uuid);
    }

    doCallChange = () => {
        if (this.props.onChange) this.props.onChange(this.state);
    }

    writeWhitelist = () => {
        this.setState(s => ({
            whitelist: s.whitelistArray.map(t => t.value).join(",")
        }), this.doCallChange);
    };

    normalizeTag = (tag) => {
        const v = (tag?.value ?? tag?.label ?? "").trim();
        return v ? { label: v, value: v } : null;
    };

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        }, this.doCallChange);
    }

    onAddressChange = (objectName, values) => {
        this.setState({
            [objectName]: {
                ...values
            }
        }, this.doCallChange);
    }

    onWhiteListDelete = (i) => {
        this.setState(s => ({ whitelistArray: s.whitelistArray.filter((_, idx) => idx !== i) }), this.writeWhitelist);
    };

    onWhitelistAdd = (tag) => {
        const norm = this.normalizeTag(tag);
        if (!norm) return;
        this.setState(s => {
            const exists = s.whitelistArray.some(t => t.value.toLowerCase() === norm.value.toLowerCase());
            return exists ? null : { whitelistArray: [...s.whitelistArray, norm] };
        }, this.writeWhitelist);
    };

    onPermissionChange = (e) => {
        const perm = e.target.name;
        const checked = !!e.target.value;

        this.setState(prev => {
            const activeSet = new Set(prev.active || []);
            const sourceSet = new Set(prev.sources || []);

            // update active
            if (checked) activeSet.add(perm);
            else activeSet.delete(perm);

            // update changes tracking: only track when it differs from original sources
            const inSource = sourceSet.has(perm);
            const nowActive = activeSet.has(perm);

            if (inSource === nowActive) delete this.changes[perm];
            else this.changes[perm] = nowActive;

            return { active: Array.from(activeSet) };
        }, this.doCallChange);
    }

    formatError = (body, fallback) => {
        if (!body) return fallback;
        if (typeof body === "string") return body;
        if (body.message) return body.message;
        if (body.error) return this.formatError(body.error, fallback);
        return fallback;
    }

    showError = (title, body) => {
        this.setState(prev => ({
            ...prev,
            modal: {
                ...prev.modal,
                error: {
                    show: true,
                    title,
                    body: this.formatError(body, "The requested change could not be saved.")
                }
            }
        }));
    }

    assertSuccess = (result, title, fallback) => {
        if (result && result.success !== false && !result.fatal) return true;
        this.showError(title, result?.message || result?.error || fallback);
        return false;
    }

    onSaveClick = async () => {
        this.props.loading(true);
        try {
            let {
                uuid, userUuid, email, phone, fax, whitelist,
                firstName, middleName, lastName, generation, residenceUuid, Residence
            } = this.state;

            const resetRequired = false;

            if (this.state.email !== this.state.originalEmail) {
                const conflictingUser = await this.props.getUser({ email: this.state.email });

                // robust existence check
                const exists = conflictingUser && conflictingUser.success !== false && (conflictingUser.userUuid || conflictingUser.uuid);

                if (exists) {
                    if (conflictingUser.hasAccess) { // FIXED typo
                        this.showError("User already exists", "This email address is already in use with another user.");
                        return;
                    } else {
                        userUuid = conflictingUser.userUuid ?? conflictingUser.uuid;
                        const applyResult = await this.props.applyToInstance({ userUuid });
                        if (!this.assertSuccess(applyResult, "Apply failed", "Unable to apply this user to the current instance.")) return;
                    }
                }
            }

            const resultingUser = await this.props.saveUser({
                email, userUuid, phone, whitelist, resetRequired
            });
            if (!this.assertSuccess(resultingUser, "Save failed", "Unable to save user information.")) return;

            userUuid = resultingUser?.uuid ?? userUuid;
            if (!isGuid(userUuid)) {
                this.showError("Save failed", "User save succeeded but no valid userUuid was returned.");
                return;
            }

            const personResult = await this.props.savePerson({
                uuid, userUuid, email, phone, fax, whitelist,
                firstName, middleName, lastName, generation, residenceUuid, Residence
            });
            if (!this.assertSuccess(personResult, "Save failed", "Unable to save person information.")) return;

            const keys = Object.keys(this.changes);
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                const permissionResult = this.changes[key]
                    ? await this.props.grant(userUuid, key)
                    : await this.props.deny(userUuid, key);
                if (!this.assertSuccess(permissionResult, "Permission save failed", `Unable to save ${key}.`)) return;
            }

            window.location.href = `/dashboard/users/${userUuid}`;
        } finally {
            this.props.loading(false);
        }
    }

    onErrorHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                error: {
                    ...prev.modal.error,
                    show: false
                }
            }
        }))
    }

    onResetButtonEnabled = () => {
        return isGuid(this.uuid); 
    }

    onResetClick = (e) => {
        
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                reset: {
                    title: "Reset user password",
                    body: `This action will cause an email with reset instructions to be sent to the user at ${this.state.email}.`,
                    show: true
                }
            }
        });
    }

    onResetOkModal = async () => {
        try {
            this.props.loading(true);
            await this.props.resetPassword(this.state.email);
        } finally {
            this.props.loading(false);
            this.onResetHideModal();
        }
    }

    onResetHideModal = (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                reset: {
                    show: false
                }
            }
        });
    }


    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Reset Password" display={true} enabled={this.onResetButtonEnabled} onEnabledClick={this.onResetClick} icon="at-sign">Reset Password</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Save User" display={true} enabled={true} onEnabledClick={this.onSaveClick} icon="save">Save</ConditionalButton>
                </Col>
                
                <Col xs="12">
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Personal Information</h2>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="4">
                                    <div className="mb-3 top-label">
                                        <Form.Control type="text" name="firstName" value={this.state.firstName || ""} onChange={this.onChange} />
                                        <Form.Label>FIRST NAME</Form.Label>
                                    </div>
                                </Col>
                                <Col lg="2">
                                    <div className="mb-3 top-label">
                                        <Form.Control type="text" name="middleName" value={this.state.middleName || ""} onChange={this.onChange} />
                                        <Form.Label>MIDDLE NAME</Form.Label>
                                    </div>
                                </Col>
                                <Col lg="4">
                                    <div className="mb-3 top-label">
                                        <Form.Control type="text" name="lastName" value={this.state.lastName || ""} onChange={this.onChange} />
                                        <Form.Label>LAST NAME</Form.Label>
                                    </div>
                                </Col>
                                <Col lg="2">
                                    <div className="mb-3 top-label">
                                        <GenerationsSelect value={this.state.generation || ""} onChange={this.onGenerationChange} placeholder="" name="generation" />
                                        <Form.Label>SUFFIX</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control type="text" name="email" value={this.state.email || ""} onChange={this.onChange} />
                                        <Form.Label>EMAIL ADDRESS</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="12">
                                    <div className="mb-3 top-label">
                                        <ReactTags
                                            selected={this.state.whitelistArray}   // [{ label, value }]
                                            // suggestions={...}                   // optional, same {label,value} shape
                                            allowNew
                                            onDelete={this.onWhiteListDelete}
                                            onAdd={this.onWhitelistAdd}
                                            placeholderText="Type a new value"
                                            activateFirstOption
                                            collapseOnSelect
                                            delimiterKeys={["Enter", "Tab", ","]}
                                            newOptionText="%value%"
                                        />
                                        <Form.Label>LOGIN FROM (WHITELIST)</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col lg="12">
                            <h2 className="small-title">Address</h2>
                        </Col>
                    </Row>
                
                    <Address {...this.state?.Residence} onChange={(values) => this.onAddressChange("Residence", values)} />
                
                    <Row className="g-3">
                        <Col md="6" lg="4" xxl="3"><h2 className="small-title">Phone #</h2>
                            <div className="mb-3">
                                <PhoneInput name="phone" id="phone" defaultCountry={this.defaultCountry} value={this.state.phone || ""} onChange={(value) => this.onChange({
                                    target: {
                                        name: "phone",
                                        value
                                    }
                                })} />
                            </div>
                        </Col>
                        <Col md="6" lg="4" xxl="3"><h2 className="small-title">Fax #</h2>
                            <div className="mb-3 top-label">
                                <PhoneInput name="fax" id="fax" defaultCountry={this.defaultCountry} value={this.state.fax || ""} onChange={(value) => this.onChange({
                                    target: {
                                        name: "fax",
                                        value
                                    }
                                })} />
                            </div>
                        </Col>
                    </Row>


                    <Row className="g-3">
                        <Col xs="12">
                            <h2 className="small-title">Permissions</h2>
                        </Col>
                        <Col xs="12">
                            <Permissions permissions={this.state.permissions} active={this.state.active} onChange={ this.onPermissionChange } />
                        </Col>
                    </Row>
                </Col>
                <ErrorModal show={this.state.modal.error.show} title={this.state.modal.error.title} body={this.state.modal.error.body} buttons={
                    [
                        {
                            default: true,
                            caption: "ok",
                            onClick: this.onErrorHideModal,
                            variant: "secondary"
                        }
                    ]
                }></ErrorModal>
                <ResetModal show={this.state.modal.reset.show} title={this.state.modal.reset.title} body={this.state.modal.reset.body} buttons={
                    [
                        {
                            default: true,
                            caption: "ok",
                            onClick: this.onResetOkModal,
                            variant: "primary"
                        },
                        {
                            caption: "cancel",
                            onClick: this.onResetHideModal,
                            variant: "secondary"
                        }
                    ]
                }></ResetModal>
            </>
        );
    }
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(connect(mapStateToProps, { pageInfo, loading, getUser, listPermissions, userPermissions, grant, deny, applyToInstance, saveUser, savePerson, resetPassword })(Detail));
